import type {
  BankAdjustmentInput,
  BankAutoMatchResult,
  BankCandidate,
  BankMatchInput,
  BankReconciliationReport,
  BankStatement,
  BankStatementCreate,
  BankStatementLineInput,
  BankStatementListItem,
  BankStatementListSummary,
  BankStatementSummaryQuery,
  BankStatementUpdate,
  Page,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import { invalidateLedger } from '../accounting/queries'
import type { BankStatementListParams } from './search'

/** Bank statements and what hangs off them (report, candidates) live under one key. */
export const bankKeys = {
  all: ['bank-statements'] as const,
  lists: () => [...bankKeys.all, 'list'] as const,
  list: (query: BankStatementListParams) => [...bankKeys.all, 'list', query] as const,
  summary: (query: BankStatementSummaryQuery) => [...bankKeys.lists(), 'summary', query] as const,
  detail: (id: string) => [...bankKeys.all, 'detail', id] as const,
  report: (id: string) => [...bankKeys.all, 'report', id] as const,
  candidates: (id: string) => [...bankKeys.all, 'candidates', id] as const,
  candidatesFor: (id: string, lineId: string, windowDays: number) =>
    [...bankKeys.all, 'candidates', id, lineId, windowDays] as const,
}

const path = (id: string, ...rest: string[]) =>
  `/bank-statements/${[id, ...rest].map(encodeURIComponent).join('/')}`

export function bankStatementListQueryOptions(query: BankStatementListParams) {
  return queryOptions({
    queryKey: bankKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<BankStatementListItem>>('/bank-statements', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          treasuryAccountId: query.treasuryAccountId,
          status: query.status,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Statements per status and the lines still to match, for the list's filters other than the status. */
export function bankStatementSummaryQueryOptions(list: BankStatementListParams) {
  const query: BankStatementSummaryQuery = {
    q: list.q,
    treasuryAccountId: list.treasuryAccountId,
    from: list.from,
    to: list.to,
  }
  return queryOptions({
    queryKey: bankKeys.summary(query),
    queryFn: ({ signal }) =>
      api.get<BankStatementListSummary>('/bank-statements/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

export function bankStatementQueryOptions(id: string) {
  return queryOptions({
    queryKey: bankKeys.detail(id),
    queryFn: ({ signal }) => api.get<BankStatement>(path(id), { signal }),
  })
}

export function bankReportQueryOptions(id: string) {
  return queryOptions({
    queryKey: bankKeys.report(id),
    queryFn: ({ signal }) => api.get<BankReconciliationReport>(path(id, 'report'), { signal }),
  })
}

/** Book lines that could match a statement line: within ±`windowDays` of its date, exact amounts first. */
export function bankCandidatesQueryOptions(id: string, lineId: string, windowDays: number) {
  return queryOptions({
    queryKey: bankKeys.candidatesFor(id, lineId, windowDays),
    queryFn: ({ signal }) =>
      api.get<BankCandidate[]>(path(id, 'candidates'), { signal, query: { lineId, windowDays } }),
    placeholderData: keepPreviousData,
  })
}

/**
 * After any change: the statement from the answer, and everything derived from it refetched (the
 * list's counts, the report, the candidates). Adjustments post and reverse entries on the bank's
 * ledger account, so the ledger and the cash & bank balances follow.
 */
async function refresh(queryClient: QueryClient, statement: BankStatement, ledger = false) {
  queryClient.setQueryData(bankKeys.detail(statement.id), statement)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: bankKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: bankKeys.report(statement.id) }),
    queryClient.invalidateQueries({ queryKey: bankKeys.candidates(statement.id) }),
    ledger ? invalidateLedger(queryClient) : undefined,
  ])
}

export function useCreateBankStatement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BankStatementCreate) => api.post<BankStatement>('/bank-statements', input),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

export function useUpdateBankStatement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BankStatementUpdate) => api.put<BankStatement>(path(id), input),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

export function useAddBankLines(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (lines: BankStatementLineInput[]) => api.post<BankStatement>(path(id, 'lines'), { lines }),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

export function useDeleteBankLine(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (lineId: string) => api.delete<BankStatement>(path(id, 'lines', lineId)),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

export function useMatchBankLines(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BankMatchInput) => api.post<BankStatement>(path(id, 'matches'), input),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

/** Unmatching an adjustment reverses its entry, so the ledger is refreshed too. */
export function useUnmatchBankLines(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (matchId: string) => api.delete<BankStatement>(path(id, 'matches', matchId)),
    onSuccess: (statement) => refresh(queryClient, statement, true),
  })
}

export function useAutoMatchBankLines(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (windowDays: number) => api.post<BankAutoMatchResult>(path(id, 'auto-match'), { windowDays }),
    // The answer is only the counts: the statement and everything derived from it are refetched.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: bankKeys.all }),
  })
}

export function useBankAdjustment(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ lineId, input }: { lineId: string; input: BankAdjustmentInput }) =>
      api.post<BankStatement>(path(id, 'lines', lineId, 'adjustment'), input),
    onSuccess: (statement) => refresh(queryClient, statement, true),
  })
}

export function useCompleteBankStatement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<BankStatement>(path(id, 'complete')),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}

export function useReopenBankStatement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => api.post<BankStatement>(path(id, 'reopen'), { reason }),
    onSuccess: (statement) => refresh(queryClient, statement),
  })
}
